import React, { useEffect, useRef, useState } from 'react';
import { motion, useInView, useReducedMotion } from 'framer-motion';
import { industryExperience, productDesignYears, shareOfIndustryExperience, totalDesignYears } from '../data/experience';

/** Segment tones — solid, on-brand, and distinguishable without new hues. */
const TONES = ['bg-foreground', 'bg-cobalt', 'bg-[#C9A66B]'] as const;
function formatYears(value: number): string {
  return Number.isInteger(value) ? String(value) : value.toFixed(1);
}
function round(value: number): number {
  return Math.round(value * 10) / 10;
}

/** Counts from 0 to `target` once `active` becomes true. */
function useCountUp(target: number, active: boolean, duration = 1100): number {
  const reduceMotion = useReducedMotion();
  const [value, setValue] = useState(0);
  useEffect(() => {
    if (!active) return;
    if (reduceMotion) {
      setValue(target);
      return;
    }
    let frame = 0;
    const start = performance.now();
    const tick = (now: number) => {
      const progress = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setValue(target * eased);
      if (progress < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [active, target, duration, reduceMotion]);
  return value;
}
export function ExperienceGraph() {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, {
    once: true,
    margin: '-80px'
  });
  const animatedProduct = useCountUp(productDesignYears, inView);
  const animatedTotal = useCountUp(totalDesignYears, inView);
  return <div ref={ref} data-id="element-274" data-mp="12wresadg9gs4">
      {/* Equal-scale KPIs: product design leads through weight, not size. */}
      <div className="grid grid-cols-1 gap-6 border-b border-border pb-8 sm:grid-cols-2 sm:gap-8 mb-10" data-id="element-275" data-mp="17vu8iynzc0ks">
        <div className="flex items-end justify-start gap-4" data-id="element-276" data-mp="2x6v1itkuacht">
          <span className="text-3xl font-bold tracking-tight leading-none tabular-nums md:text-4xl" style={{
          fontSize: "36px",
          fontWeight: "400"
        }} data-id="element-277" data-mp="326g218fnqswn">
            {formatYears(inView ? round(animatedProduct) : 0)}
          </span>
          <span className="max-w-[10rem] text-left text-sm leading-snug text-muted" data-id="element-278" data-mp="2vtllhkkbp5f1">
            years designing digital products
          </span>
        </div>

        <div className="flex items-end justify-start gap-4" data-id="element-279" data-mp="2lue6ksxxx2pt">
          <span className="text-3xl font-normal tracking-tight leading-none tabular-nums text-muted md:text-4xl" style={{
          fontSize: "36px"
        }} data-id="element-280" data-mp="3jgtofvua42dw">
            {formatYears(inView ? round(animatedTotal) : 0)}
          </span>
          <span className="max-w-[10rem] text-left text-sm leading-snug text-muted" data-id="element-281" data-mp="39qi8tc18g4r6">
            years across design overall
          </span>
        </div>
      </div>

      {/* Proportional bar — all career industries; KPI scope remains distinct. */}
      <div className="flex h-3 w-full overflow-hidden rounded-full bg-border mb-10" aria-hidden="true" data-id="element-282" data-mp="649u70bm87el">
        {industryExperience.map((item, i) => <motion.div key={item.industry} className={TONES[i % TONES.length]} initial={{
        width: 0
      }} animate={{
        width: inView ? `${shareOfIndustryExperience(item.years)}%` : 0
      }} transition={{
        duration: 0.9,
        delay: 0.1 + i * 0.08,
        ease: [0.22, 1, 0.36, 1]
      }} data-id="element-283" data-mp="9lp3amdu9440" data-mpi={i} />)}
      </div>

      {/* Breakdown */}
      <dl className="border-t border-border" data-id="element-284" data-mp="wk23cs91chnj">
        {industryExperience.map((item, i) => <IndustryRow key={item.industry} item={item} tone={TONES[i % TONES.length]} active={inView} data-id="element-285" data-mp="11bk1z9nl665c" data-mpi={i} />)}
      </dl>
    </div>;
}
function IndustryRow({
  item,
  tone,
  active
}: {
  item: (typeof industryExperience)[number];
  tone: string;
  active: boolean;
}) {
  const years = useCountUp(item.years, active);
  return <div className="flex items-baseline justify-between gap-6 py-5 border-b border-border" data-id="element-286" data-mp="2czoej7gxfomz">
      <div className="flex items-baseline gap-3 min-w-0" data-id="element-287" data-mp="280uzhalzrpkp">
        <span className={`w-2.5 h-2.5 rounded-full shrink-0 translate-y-[-1px] ${tone}`} aria-hidden="true" data-id="element-288" data-mp="280bralm58fjv" />
        <div className="min-w-0" data-id="element-289" data-mp="2hqo8q4ynvsls">
          <dt className="text-base md:text-lg font-medium break-words" data-id="element-290" data-mp="2hrpm4g35gr6o">
            {item.industry}
          </dt>
          <dd className="text-sm text-muted mt-1 break-words" data-id="element-291" data-mp="1u1veccxotsta">
            {item.companies} · {item.range}
          </dd>
        </div>
      </div>

      <dd className="flex items-baseline gap-1.5 shrink-0 tabular-nums" data-id="element-292" data-mp="231s6iojad9t3">
        <span className="text-2xl md:text-3xl font-bold tracking-tight" style={{
        fontWeight: "400"
      }} data-id="element-293" data-mp="22znrbafxsodi">
          {formatYears(active ? round(years) : 0)}
        </span>
        <span className="text-sm text-muted" data-id="element-294" data-mp="3efm6zx5me8np">
          {item.years === 1 ? 'yr' : 'yrs'}
        </span>
      </dd>
    </div>;
}